import { apiClient as api } from './api';

export interface Expense {
  id: string;
  category: string;
  description: string;
  amount: number;
  gstAmount?: number;
  paymentMethod: string;
  receiptNumber?: string;
  vendorName?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  expenseDate: string;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  approver?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreateExpenseData {
  category: string;
  description: string;
  amount: number;
  gstAmount?: number;
  paymentMethod: string;
  receiptNumber?: string;
  vendorName?: string;
  expenseDate: string;
}

export interface UpdateExpenseData {
  category?: string;
  description?: string;
  amount?: number;
  gstAmount?: number;
  paymentMethod?: string;
  receiptNumber?: string;
  vendorName?: string;
  expenseDate?: string;
}

export interface ExpenseFilters {
  category?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  paymentMethod?: string;
  vendorName?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  createdBy?: string;
  approvedBy?: string;
  page?: number;
  limit?: number;
}

export interface ExpenseStats {
  totalExpenses: number;
  totalAmount: number;
  totalGstAmount: number;
  expensesByCategory: Array<{
    category: string;
    count: number;
    totalAmount: number;
  }>;
  expensesByStatus: Array<{
    status: string;
    count: number;
    totalAmount: number;
  }>;
  expensesByPaymentMethod: Array<{
    paymentMethod: string;
    count: number;
    totalAmount: number;
  }>;
  monthlyExpenses: Array<{
    month: string;
    totalAmount: number;
    count: number;
  }>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class ExpenseService {
  /**
   * Create a new expense
   */
  async createExpense(data: CreateExpenseData): Promise<Expense> {
    const response = await api.post('/expenses', data);
    return response.data;
  }

  /**
   * Get expense by ID
   */
  async getExpenseById(id: string): Promise<Expense> {
    const response = await api.get(`/expenses/${id}`);
    return response.data;
  }

  /**
   * Get expenses with filtering and pagination
   */
  async getExpenses(filters: ExpenseFilters = {}): Promise<PaginatedResponse<Expense>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/expenses?${params.toString()}`);

    // Handle the API response structure
    return {
      data: response.data || [],
      pagination: response.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
      },
    };
  }

  /**
   * Update expense
   */
  async updateExpense(id: string, data: UpdateExpenseData): Promise<Expense> {
    const response = await api.put(`/expenses/${id}`, data);
    return response.data;
  }

  /**
   * Delete expense
   */
  async deleteExpense(id: string): Promise<void> {
    await api.delete(`/expenses/${id}`);
  }

  /**
   * Approve expense
   */
  async approveExpense(id: string): Promise<Expense> {
    const response = await api.post(`/expenses/${id}/approve`);
    return response.data;
  }

  /**
   * Reject expense
   */
  async rejectExpense(id: string): Promise<Expense> {
    const response = await api.post(`/expenses/${id}/reject`);
    return response.data;
  }

  /**
   * Get expense statistics
   */
  async getExpenseStats(startDate?: string, endDate?: string): Promise<ExpenseStats> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/expenses/stats?${params.toString()}`);
    return response.data;
  }

  /**
   * Get expense categories
   */
  async getExpenseCategories(): Promise<string[]> {
    const response = await api.get('/expenses/categories');
    return response.data;
  }

  /**
   * Get vendors
   */
  async getVendors(): Promise<string[]> {
    const response = await api.get('/expenses/vendors');
    return response.data;
  }

  /**
   * Bulk approve expenses
   */
  async bulkApproveExpenses(expenseIds: string[]): Promise<{ approvedCount: number }> {
    const response = await api.post('/expenses/bulk/approve', { expenseIds });
    return response.data;
  }

  /**
   * Get pending expenses
   */
  async getPendingExpenses(): Promise<Expense[]> {
    const response = await api.get('/expenses/pending');
    return response.data;
  }
}

export const expenseService = new ExpenseService();
